import { createStore, type StoreApi } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { DEFAULT_DENSITY, DEFAULT_THEME, THEMES, type Density, type ThemeId } from '../theme/themes';

export type TimeZoneMode = 'server' | 'local' | 'utc';
export type NumberFormat = 'en' | 'de' | 'fr' | 'plain';

/**
 * Everything the terminal remembers for a user. It is one JSON object (max 48 KB on the server) so that adding a setting
 * never needs a migration: unknown keys are kept, missing ones fall back to defaults.
 */
export interface PrefsData {
  /** A theme, or 'system' to follow the device's light or dark setting. */
  theme?: ThemeId | 'system';
  density?: Density;
  /** Which clock times are shown in (the chart's time axis is always server time). */
  timeZone?: TimeZoneMode;
  /** How amounts of money are written. */
  numberFormat?: NumberFormat;
  /** What the browser tab title shows. */
  tabTitle?: 'symbol' | 'profit' | 'off';
  /** dockview layout */
  layout?: unknown;
  /** The desktop workspace: the classic trading-terminal look (menu bar, toolbar, docked windows) or the Adiox one. */
  shell?: 'classic' | 'modern';
  /** The workspace the saved layout was made for (older layouts, without it, are the Adiox one). */
  layoutShell?: 'classic' | 'modern';
  /** Indicators the user starred in the Navigator's Favorites. */
  navigatorFavorites?: string[];
  marketWatch?: { symbols: string[] };
  /** The Market Watch columns shown after the symbol. */
  marketWatchColumns?: string[];
  /** The trader saw or skipped the first-run guide. */
  guideSeen?: boolean;
  /** The manager portal's side menu is folded to a narrow strip. */
  managerNavCollapsed?: boolean;
  /** The manager portal's workspace: the classic manager (the default) or the Adiox frame. */
  managerShell?: 'classic' | 'modern';
  /** The classic manager's panes: which are shown and their sizes. */
  managerPanes?: unknown;
  /** The symbols in the classic manager's Market Watch. */
  managerMarketWatch?: string[];
  /** The classic manager's Tools > Options (sounds on events, hide online users, ...). */
  managerOptions?: unknown;
  /** The classic manager's saved window layouts (Window > Window Layouts). */
  managerWindowLayouts?: unknown;
  /** The phone layout's chart (symbol, timeframe, indicators…), apart from the desktop layout's charts. */
  mobileChart?: unknown;
  /** The user has read and accepted the one-click trading notice. */
  oneClickAck?: boolean;
  /** The trading account the terminal shows when the user has more than one. */
  activeLogin?: number;
  /** Whether notifications also pop up as toasts (the log always records them). */
  notifyToasts?: boolean;
  /** Sounds: none, only warnings and alerts (the default), or every notification. */
  notifySound?: 'off' | 'alerts' | 'all';
  /** Desktop notifications while the terminal is in a background tab (the browser asks for permission once). */
  notifyDesktop?: boolean;
  /** Drawings by symbol (shared by every chart and timeframe of that symbol). */
  drawings?: Record<string, unknown>;
  /** Named chart templates. */
  chartTemplates?: Record<string, unknown>;
  /** Ask before a market order from the order ticket is sent (one-click trading on a chart never asks). */
  confirmMarketOrders?: boolean;
  /** Ask before a single position is closed from the Trade panel. */
  confirmClose?: boolean;
  /** Price alerts (checked by the terminal while it is open, like MT5's Alerts tab). */
  priceAlerts?: unknown[];
  [key: string]: unknown;
}

export type SyncStatus = 'local' | 'loading' | 'synced' | 'saving' | 'error';

export interface PrefsState {
  data: PrefsData;
  /** The server revision this data was last based on (0 = the server has none yet). */
  revision: number;
  status: SyncStatus;
  /** Top-level keys changed here since the last successful save (used to merge after a conflict). */
  dirty: string[];
}

export interface PrefsStore extends StoreApi<PrefsState> {
  set<K extends string>(key: K, value: PrefsData[K]): void;
  /** Replaces the data with what the server holds (not marked dirty). */
  hydrate(data: PrefsData, revision: number): void;
  markSaved(revision: number): void;
}

const CACHE_KEY = 'adiox.prefs.v2';

export function readCache(): PrefsData {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') as unknown;
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as PrefsData) : {};
  } catch {
    return {};
  }
}

function writeCache(data: PrefsData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // storage may be blocked (private mode): the server copy is the durable one
  }
}

export function createPrefsStore(initial: PrefsData = readCache()): PrefsStore {
  const store = createStore<PrefsState>(() => ({ data: initial, revision: 0, status: 'local', dirty: [] }));
  const set: PrefsStore['set'] = (key, value) => {
    store.setState((s) => {
      const data = { ...s.data, [key]: value };
      writeCache(data);
      return { data, dirty: s.dirty.includes(key) ? s.dirty : [...s.dirty, key] };
    });
  };
  return Object.assign(store, {
    set,
    hydrate(data: PrefsData, revision: number) {
      writeCache(data);
      store.setState({ data, revision, dirty: [], status: 'synced' });
    },
    markSaved(revision: number) {
      store.setState((s) => ({ revision, dirty: [], status: s.status === 'error' ? 'error' : 'synced' }));
    },
  });
}

/** The app-wide store (tests create their own). */
export const prefsStore = createPrefsStore();

export function usePrefsValue<T>(selector: (state: PrefsState) => T): T {
  return useStore(prefsStore, selector);
}

// ---------------------------------------------------------------- typed accessors
/** The theme choice as saved: a theme, or 'system'. */
export const themeChoiceOf = (d: PrefsData): ThemeId | 'system' =>
  d.theme === 'system' ? 'system' : d.theme && d.theme in THEMES ? d.theme : DEFAULT_THEME;

/** The theme to show: 'system' becomes the dark default or the light theme, as the device prefers. */
export const themeOf = (d: PrefsData): ThemeId => {
  const choice = themeChoiceOf(d);
  if (choice !== 'system') return choice;
  const light = typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches;
  return light ? 'light' : DEFAULT_THEME;
};
export const shellOf = (d: PrefsData): 'classic' | 'modern' => (d.shell === 'modern' ? 'modern' : 'classic');
export const timeZoneOf = (d: PrefsData): TimeZoneMode =>
  d.timeZone === 'local' || d.timeZone === 'utc' ? d.timeZone : 'server';
export const numberFormatOf = (d: PrefsData): NumberFormat =>
  d.numberFormat === 'de' || d.numberFormat === 'fr' || d.numberFormat === 'plain' ? d.numberFormat : 'en';
export const densityOf = (d: PrefsData): Density =>
  d.density === 'compact' || d.density === 'comfortable' ? d.density : DEFAULT_DENSITY;
export const symbolsOf = (d: PrefsData): string[] | null =>
  Array.isArray(d.marketWatch?.symbols) ? d.marketWatch.symbols.filter((s) => typeof s === 'string') : null;
